import { useQuery } from '@apollo/client'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Button, Table as BaseTable } from '@/components_deprecated'
import { subWeeks } from 'date-fns'
import { EXPIRATION_TYPES } from '@/modules/vehicles/consts'
import { VEHICLE_EXPIRATIONS_QUERY } from '@/modules/vehicles/queries'
import type {
  EditVehicleExpirationForm,
  EditVehicleExpirationPromptResolve,
} from '@/modules/vehicles/types'
import type {
  VehicleExpirationNode,
  VehicleExpirationsQuery,
  VehicleExpirationsQueryVariables,
} from '@/modules/vehicles/types.graphql'
import { useMemo } from 'react'
import styled from 'styled-components'
import { format, formatDistanceStrict } from '@/utils/date-fns'
import { useConfirm, useOnErrorAuto, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useVehicleExpirationMutations } from '../mutations.hooks'
import { EditVehicleExpirationModal } from './EditVehicleExpirationModal'
import { VehicleExpirationFilter } from './VehicleExpirationFilter'
import { useVehicleExpirationsFilterForm } from '../hooks'
import { getVehicleExpirationTypeName } from '../../util'
import { isMobileOnly } from 'react-device-detect'

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  ${isMobileOnly &&
  `
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    order: -1;
  `}
`

const Wrapper = styled.div`
  margin-top: 1rem;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 0;

  ${isMobileOnly &&
  `
   flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  `}
`

const Table = styled(BaseTable)`
  position: relative;

  thead {
    text-align: left;
  }

  tbody tr {
    &:hover {
      background: ${props => props.theme.colors.gray10};
    }
  }
`

const StyledButton = styled(Button)`
  margin-left: auto;
  padding: 0 2rem;
  font-weight: 500;

  ${isMobileOnly &&
  `
    margin-left: 0;
    order: -1;
    width: 100%;
  `}
`

interface VehicleExpirationsProps {
  vehicleId: string
}

export const VehicleExpirations: React.FC<VehicleExpirationsProps> = ({
  vehicleId,
}) => {
  const translations = useTranslate({
    expirationDates: 'vehicles.expiration-dates',
    add: 'vehicles.add-expiration-date',
    type: 'common.type',
    description: 'common.description',
    expirationDate: 'common.expires-at',
    date: 'common.date',
    notificationTime: 'common.notification',
    noExpirationDates: 'vehicles.no-expiration-dates',
    prompt: {
      delete: 'vehicles.prompts.delete-expiration-date',
      deleteConfirmation: [
        'documents.prompts.delete-document-confirmation',
        { name: '' },
      ],
    },
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const onErrorAuto = useOnErrorAuto()
  const { hasPermissions } = usePermissions()
  const canEdit = hasPermissions(
    PERMISSIONS.vehicles.change.vehicleexpiration,
    true
  )
  const canDelete = hasPermissions(
    PERMISSIONS.vehicles.delete.vehicleexpiration,
    true
  )
  const { formValues: filter, updateForm } = useVehicleExpirationsFilterForm()

  const { data, loading } = useQuery<
    VehicleExpirationsQuery,
    VehicleExpirationsQueryVariables
  >(VEHICLE_EXPIRATIONS_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      vehicle: vehicleId,
      expirationTypes: filter.expirationTypes,
      orderBy: filter.orderBy ?? 'expirationDate',
    },
    onError: onErrorAuto(),
  })

  const expirations = useMemo(() => {
    return data?.allVehicleExpirations?.edges?.map(edge => edge?.node) || []
  }, [data])

  const { createExpiration, patchExpiration, deleteExpiration } =
    useVehicleExpirationMutations()

  function getExpirationAndNotificationTime(
    form: EditVehicleExpirationForm,
    hasNotification: boolean
  ) {
    const expirationDate = form.expirationTo
    const notificationTime = !hasNotification
      ? null
      : subWeeks(expirationDate, form.notificationTime)
    return {
      expirationDate,
      notificationTime,
    }
  }

  function getExpirationInput(
    form: EditVehicleExpirationForm,
    hasNotification: boolean
  ) {
    const { expirationDate, notificationTime } =
      getExpirationAndNotificationTime(form, hasNotification)

    return {
      description: form.description,
      expirationType: form.expirationType,
      responsibleUser: form.responsibleUser,
      expirationDuration: form.duration,
      expirationDate,
      notificationTime,
      expirationTo: form.expirationTo,
      expirationFrom: form.expirationFrom,
    }
  }

  async function handleCreate() {
    const { data } = await addPrompt<EditVehicleExpirationPromptResolve | null>(
      resolve => <EditVehicleExpirationModal onSubmit={resolve} />
    )
    if (!data) return

    await createExpiration({
      variables: {
        input: {
          ...getExpirationInput(data.form, data.hasNotification),
          vehicle: vehicleId,
        },
      },
    })
  }

  async function handleEdit(expiration: VehicleExpirationNode) {
    const { data } = await addPrompt<EditVehicleExpirationPromptResolve | null>(
      resolve => (
        <EditVehicleExpirationModal
          vehicleExpiration={expiration}
          onSubmit={resolve}
        />
      )
    )
    if (!data) return

    await patchExpiration({
      variables: {
        id: expiration.id,
        input: getExpirationInput(data.form, data.hasNotification),
      },
    })
  }

  async function handleDelete(expiration: VehicleExpirationNode) {
    const expirationDate = format(new Date(expiration.expirationDate), 'PP')
    const expirationType = getVehicleExpirationTypeName({ expiration })
    const confirmName = `${expirationDate} - ${expirationType}`
    const { data: answer } = await confirm(
      translations.prompt.deleteConfirmation({ name: confirmName }),
      translations.prompt.delete
    )
    if (!answer) return
    await deleteExpiration({
      variables: {
        id: expiration.id,
      },
    })
  }

  return (
    <Wrapper>
      <Header>
        <h2>{translations.expirationDates}</h2>
        <Controls>
          <VehicleExpirationFilter filter={filter} onUpdate={updateForm} />
          <StyledButton
            iconLeftProps={{ icon: 'plus', size: '1rem', type: 'solid' }}
            height="3rem"
            onClick={handleCreate}
          >
            {translations.add}
          </StyledButton>
        </Controls>
      </Header>
      <Table
        loading={loading}
        noShadow
        noData={!expirations.length}
        noDataText={translations.noExpirationDates}
      >
        <thead>
          <tr>
            <th>{translations.date}</th>
            <th>{translations.type}</th>
            <th>{translations.description}</th>
            <th>{translations.notificationTime}</th>
            {canEdit && <th></th>}
            {canDelete && <th></th>}
          </tr>
        </thead>
        <tbody>
          {expirations.map(expiration => {
            const expirationType = getVehicleExpirationTypeName({ expiration })
            const expirationDate = expiration.expirationDate
              ? format(new Date(expiration.expirationDate), 'PP')
              : '-'
            const notificationTime =
              !!expiration.notificationTime && !!expiration.expirationDate
                ? formatDistanceStrict(
                    new Date(expiration.expirationDate),
                    new Date(expiration.notificationTime)
                  )
                : '-'
            const canEditThisExpiration = canEdit
            const canDeleteThisExpiration =
              canDelete && expirationType !== EXPIRATION_TYPES.nextEuControl
            return (
              <tr key={expiration.id}>
                <td>{expirationDate}</td>
                <td className="wrap">{expirationType ?? '-'}</td>
                <td className="wrap">{expiration.description}</td>
                <td>{notificationTime}</td>
                {canEdit && (
                  <td width="1px" className="center">
                    {canEditThisExpiration && (
                      <Icon
                        icon="edit"
                        cursor="pointer"
                        color="gray6"
                        hoverColor="secondary"
                        onClick={() => handleEdit(expiration)}
                      />
                    )}
                  </td>
                )}
                {canDelete && (
                  <td>
                    {canDeleteThisExpiration && (
                      <Icon
                        icon="times"
                        cursor="pointer"
                        color="gray6"
                        hoverColor="red"
                        onClick={() => handleDelete(expiration)}
                      />
                    )}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </Table>
    </Wrapper>
  )
}
